// This service is responsible uses the legacy public ajax API to create a checkout session
import {CheckoutApi} from "./CheckoutApi";
import {GlobalEventBus} from "../GlobalEventBus";
import {SESSION_EVENTS} from "../../Events/Session";
import {NotConnectedError} from "./Errors/NotConnectedError";
import {IClientStorageService} from "../../Contracts/GloablServices/IClientStorageService";
import {v7 as uuidv7} from 'uuid';

/**
 * Cookie holding the full `prefix-hash` session id. Shared with the legacy widgets, which
 * read and write the same cookie via js-cookie, so the value must stay a plain string.
 */
export const SESSION_COOKIE_NAME = 'BkOpSession';

// Anything else in the cookie is not one of ours and must not reach URLs or analytics untouched.
const SESSION_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

type SessionServiceConstructorArgs = {
    checkoutApi: CheckoutApi,
    loggerService: ILogger,
    eventBus: typeof GlobalEventBus,
    sessionStorage: IClientStorageService,
}

/**
 * Best-effort prefix of a stored session id when the server hash is not known (offline): the
 * hash is appended as `-hash`, so everything before the last `-` is taken.
 */
const guessSessionPrefix = (sessionId: string): string => {
    const separatorIndex = sessionId.lastIndexOf('-');
    return separatorIndex === -1 ? sessionId : sessionId.substring(0, separatorIndex);
}

const getSuccessPageToken = ():false|string => {
    const input = window.location.hash;
    const cleaned = input.replace(/^#!/, "").replace(/^#/, "");
    // Split off anything before a "?" (e.g. paths like "!/e/...")
    const queryIndex = cleaned.indexOf("?");
    if (queryIndex === -1) return false;

    const queryString = cleaned.substring(queryIndex + 1);

    // Parse parameters
    const params = new URLSearchParams(queryString);

    const value = params.get("c2s");
    return value ? value : false;
}

export class SessionService {
    private readonly checkoutApi: CheckoutApi;
    private readonly loggerService: ILogger;
    private readonly eventBus: typeof GlobalEventBus;
    private readonly sessionStorage: IClientStorageService;

    /** Seed generated on page load. Becomes the prefix whenever the stored session cannot be reused. */
    private readonly generatedSeed: string;
    /** Full `prefix-hash` session id. Provisional until `init()` has confirmed the hash with the server. */
    private sessionId: string;
    private sessionConfirmed = false;

    private readonly sessionPrefix: Promise<string>;
    private resolveSessionPrefix!: (prefix: string) => void;

    constructor({checkoutApi, loggerService, eventBus, sessionStorage}: SessionServiceConstructorArgs) {
        this.checkoutApi = checkoutApi;
        this.loggerService = loggerService;
        this.eventBus = eventBus;
        this.sessionStorage = sessionStorage;
        this.generatedSeed = this.generateSessionSeed();
        // Reuse a stored session (possibly written by another widget) so early callers get a stable id.
        this.sessionId = this.readStoredSessionId() ?? this.generatedSeed;
        this.sessionPrefix = new Promise<string>((resolve) => {
            this.resolveSessionPrefix = resolve;
        });
    }

    generateSessionSeed(): string
    {
        try {
            return uuidv7().toString();
        } catch (e: unknown) {
            //fallback to random string
            return 'xxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        }
    }

    checkIfIsSuccessScreen()
    {
        const successToken = getSuccessPageToken();
        if(successToken)
        {
            // Emit event to notify the checkout flow is complete
            this.eventBus.emit(SESSION_EVENTS.CHECKOUT_SUCCESSFUL, {token: successToken});
        }
    }

    /**
     * Returns the prefix of the session id, i.e. the session id without the `-hash` the checkout
     * server appended. It is what the checkout app extracts from the session id as the PostHog
     * distinct id. Resolves once `init()` has settled: with the server hash the prefix is exact,
     * without it (offline) the part before the last `-` of the provisional session id is used.
     *
     * @returns The session prefix (a UUID v7 when generated by this widget)
     */
    getSessionPrefix(): Promise<string>
    {
        return this.sessionPrefix;
    }

    getSessionId(): string
    {
        if(!this.sessionConfirmed){
            this.loggerService.error(new Error('called get session when not having a valid session'))
        }
        return this.sessionId;
    }

    async init()
    {
        try {
            const res = await this.checkoutApi.createSession();
            this.sessionId = this.resolveSessionId(this.readStoredSessionId(), res.id);
            this.sessionConfirmed = true;
            // Always rewrite so the lifetime is extended from the last read.
            this.sessionStorage.set(SESSION_COOKIE_NAME, this.sessionId);
            this.resolveSessionPrefix(this.sessionId.slice(0, -(res.id.length + 1)));
            this.eventBus.emit(SESSION_EVENTS.SESSION_CREATED, {sessionId: this.getSessionId()});
            this.checkIfIsSuccessScreen();
        } catch (e: unknown) {
            // Analytics must not stay blocked on a session that never arrives.
            this.resolveSessionPrefix(this.sessionId === this.generatedSeed
                ? this.generatedSeed
                : guessSessionPrefix(this.sessionId));
            if(! (e instanceof NotConnectedError)){
                throw e;
            }
        }
    }

    private readStoredSessionId(): string | null
    {
        const stored: unknown = this.sessionStorage.get(SESSION_COOKIE_NAME);
        return typeof stored === 'string' && SESSION_ID_PATTERN.test(stored) ? stored : null;
    }

    /**
     * Keeps the stored session id when its hash still matches the one the server returned,
     * otherwise builds a brand new `prefix-hash` from the seed generated on page load. The prefix
     * format is not assumed (other widgets may write a different one), so the hash is matched as
     * a suffix instead of split.
     */
    private resolveSessionId(storedSessionId: string | null, serverHash: string): string
    {
        if (storedSessionId && storedSessionId.endsWith('-' + serverHash)) {
            return storedSessionId;
        }
        return this.generatedSeed + '-' + serverHash;
    }

}
